/**
 * TabbedToolPage Component
 *
 * Enhanced ToolPage with tab support for multi-view tool pages.
 * Maintains the same header structure but adds tab navigation.
 *
 * Features:
 * - Multiple tabs with custom content
 * - Standard card/list views per tab
 * - Custom view support for non-standard layouts
 * - Shared stats and toolbar across tabs
 * - Consistent styling with existing ToolPage
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { DashboardLayout } from "../../layouts";
import PageHeader from "./PageHeader";
import StatsWidget from "./StatsWidget";
import ToolbarGroup from "./ToolbarGroup";
import { Badge } from "./Badge";
import { toolThemeVars } from "../../utils/toolTheme";
import "./TabbedToolPage.scss";

const TabbedToolPage = ({
  tool,
  tabs,
  defaultTab,
  onRefresh,
  loading = false,
  error = null,
  onRetry,
  stats = null,
  toolbar = null,
  actions = null,
  className = "",
  // Opt-in tool theming (same contract as ToolPage): exposes the tool's
  // accent as CSS custom properties, tinting the tab bar plus any shared
  // descendants (Button, SegmentedControl…) that honour --tool-color.
  // Tools opt in as they're updated; everything else keeps the default.
  themed = false,
}) => {
  // Remember active tab in localStorage using tool name as key
  const storageKey = `tabbedToolPage_${tool.name}_activeTab`;
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved || defaultTab || tabs[0]?.id;
  });

  // Persist tab selection
  useEffect(() => {
    localStorage.setItem(storageKey, activeTab);
  }, [activeTab, storageKey]);

  // A remembered tab that is not offered now (an administrators' tab, remembered in this browser
  // and then opened by someone else) falls back to the default, never to an empty page.
  const currentTab = tabs.find((tab) => tab.id === activeTab) || tabs.find((tab) => tab.id === defaultTab) || tabs[0];

  return (
    <DashboardLayout>
      <div
        className={`tabbed-tool-page ${themed ? "tabbed-tool-page--themed" : ""} ${className}`}
        style={themed ? toolThemeVars(tool.color) : undefined}
      >
        <PageHeader
          icon={tool.icon}
          iconColor={tool.color}
          title={tool.name}
          description={tool.description}
          onRefresh={onRefresh}
          loading={loading}
          actions={actions || []}
          showFullscreenToggle={true}
        />

        {error && (
          <div className="tool-page-error">
            <p>Error: {error}</p>
            {onRetry && (
              <button onClick={onRetry} className="retry-btn">
                Try Again
              </button>
            )}
          </div>
        )}

        {/* Tab navigation - moved above stats */}
        <div className="tab-navigation">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab-button ${currentTab && currentTab.id === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                disabled={tab.disabled}
              >
                {IconComponent && (
                  <span className="tab-icon">
                    <IconComponent size={18} />
                  </span>
                )}
                <span>{tab.label}</span>
                {tab.badge && (
                  <Badge variant="purple" size="small">
                    {tab.badge}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>

        {/* Stats + optional toolbar */}
        {toolbar || currentTab?.toolbar ? (
          // If there's a toolbar, wrap stats + toolbar in ToolbarGroup
          <ToolbarGroup>
            {(currentTab?.stats || stats) && (
              <StatsWidget
                stats={currentTab?.stats || stats}
                variant="bar"
                loading={loading}
                showIcons
              />
            )}
            {currentTab?.toolbar || toolbar}
          </ToolbarGroup>
        ) : (
          // If no toolbar, just render stats (toolbar will be in DataTable below)
          (currentTab?.stats || stats) && (
            <StatsWidget
              stats={currentTab?.stats || stats}
              variant="bar"
              loading={loading}
              showIcons
            />
          )
        )}

        {/* Tab content */}
        <div className="tab-content">
          {currentTab?.render ? currentTab.render() : <div>No content</div>}
        </div>
      </div>
    </DashboardLayout>
  );
};

TabbedToolPage.propTypes = {
  /** Tool object from toolRegistry */
  tool: PropTypes.shape({
    icon: PropTypes.elementType.isRequired,
    color: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,

  /** Array of tab configurations */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      disabled: PropTypes.bool,
      toolbar: PropTypes.node, // Optional toolbar specific to this tab
      render: PropTypes.func.isRequired, // Function that returns tab content
    })
  ).isRequired,

  /** Default active tab ID */
  defaultTab: PropTypes.string,

  /** Refresh callback */
  onRefresh: PropTypes.func,

  /** Loading state */
  loading: PropTypes.bool,

  /** Error message */
  error: PropTypes.string,

  /** Retry callback for error state */
  onRetry: PropTypes.func,

  /** Optional stats to display above tabs (shared across all tabs) */
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      icon: PropTypes.elementType,
      color: PropTypes.string,
    })
  ),

  /** Optional toolbar to display above tab content (shared across all tabs) */
  toolbar: PropTypes.node,

  /** Page-level actions rendered top-right in the header. Either the array of
   *  button configs PageHeader accepts, or a ready-made node (for tools whose
   *  header controls are toggles/segments rather than plain buttons). */
  actions: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),

  /** Additional CSS class */
  className: PropTypes.string,
};

export default TabbedToolPage;
